import React, { forwardRef } from 'react';
import { Box, Button, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ColorDot from '../colorDot';
import { GameStatus } from '../../../constants/gameConstants';
import {
  getMatchLocationLabel,
  getMatchParties,
  getMatchScore,
  getParticipantStatusLabel
} from '../../../helpers/bracketData';

const MatchCard = forwardRef(function MatchCard(
  { match, game, bracketId, authenticated, isMobile = false, loading = false, scheduleText = '', onEdit, onAddGame, onViewGame },
  ref
) {
  const {
    isWalkOver,
    missingName,
    topMissing,
    bottomMissing,
    topParty,
    bottomParty,
    hasRealParticipants
  } = getMatchParties(match);

  const score = getMatchScore(match, game);
  const locationLabel = getMatchLocationLabel(match);
  // A stage can play to a different format than the sections (for example a
  // longer semifinal and final); show it so the node says so before a game is
  // created from it.
  const formatLabel = match.gameFormat?.name ? `Format: ${match.gameFormat.name}` : '';
  const detailLabel = [locationLabel, formatLabel, scheduleText].filter(Boolean).join(' · ');
  const canBeGame = hasRealParticipants && match.state === 'PENDING';
  const gameDone = game?.status === GameStatus.COMPLETE;

  let teamAColor = '';
  let teamBColor = '';
  if (game) {
    teamAColor = game.teams?.teamA?.color || '';
    teamBColor = game.teams?.teamB?.color || '';
  }

  const topWon = Boolean(topParty.isWinner);
  const bottomWon = Boolean(bottomParty.isWinner);
  let topResult = topParty.resultText || '';
  let bottomResult = bottomParty.resultText || '';
  let topWonResult = topWon ? '🏆' : '🥈';
  let bottomWonResult = bottomWon ? '🏆' : '🥈';

  if (topResult === 'DRAW' && bottomResult === 'DRAW') {
    topWonResult = '🏅';
    bottomWonResult = '🏅';
  }

  if (match.state === 'DONE') {
    topResult = topWonResult;
    bottomResult = bottomWonResult;
  }

  const nameSx = isMobile
    ? { whiteSpace: 'normal', overflowWrap: 'anywhere' }
    : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  const actionButtonSx = {
    minWidth: 0,
    px: isMobile ? 1.5 : 1,
    py: isMobile ? 0.6 : 0.4,
    fontSize: isMobile ? '0.8rem' : '0.7rem',
    minHeight: isMobile ? 38 : 32
  };

  const renderParty = (party, missing, color, result) => (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      gap={1}>
      <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
        <ColorDot color={color} noOffset />
        <Typography
          variant="caption"
          title={party.name}
          sx={{
            minWidth: 0,
            ...nameSx,
            ...(missing && isWalkOver ? { color: 'warning.main' } : {})
          }}>
          {party.name || missingName}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ flexShrink: 0 }}>
        {result || getParticipantStatusLabel(party) || ''}
      </Typography>
    </Box>
  );

  return (
    <Paper
      ref={ref}
      sx={{
        width: '100%',
        maxWidth: isMobile ? 'none' : 450,
        minHeight: isMobile ? 104 : 112,
        p: 1.25,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 0.5,
        // The card follows the app surface. It used to be navy in light mode,
        // which made the tree read as a dark block; light mode now uses the
        // same white card as the rest of the app.
        backgroundColor: 'background.paper',
        color: 'text.primary'
      }}
      elevation={3}>
      <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" width="100%">
        <Typography
          variant="caption"
          sx={{ minWidth: 0, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {match.name}
        </Typography>
        {authenticated && !game && (
          <IconButton
            size="small"
            aria-label={`Edit ${match.name || 'match'}`}
            color="primary"
            onClick={() => onEdit(match)}
            sx={{ ml: 0.5, p: 0.5, minWidth: isMobile ? 36 : 32, minHeight: isMobile ? 36 : 32 }}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {detailLabel ? (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          gap={0.5}
          sx={{ minWidth: 0 }}>
          {locationLabel ? (
            <LocationOnOutlinedIcon
              sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }}
            />
          ) : (
            <AccessTimeOutlinedIcon
              sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            title={detailLabel}
            sx={{
              minWidth: 0,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              textAlign: 'center'
            }}>
            {detailLabel}
          </Typography>
        </Box>
      ) : null}

      {renderParty(topParty, topMissing, teamAColor, topResult || score.top)}
      {renderParty(bottomParty, bottomMissing, teamBColor, bottomResult || score.bottom)}

      <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" width="100%">
        {loading ? (
          <CircularProgress size={24} sx={{ color: 'primary.main' }} />
        ) : canBeGame ? (
          <>
            {game ? (
              <Button
                variant="outlined"
                size="small"
                color="primary"
                sx={actionButtonSx}
                onClick={() => onViewGame(game)}>
                View Game
              </Button>
            ) : (
              authenticated && (
                <Button
                  variant="contained"
                  size="small"
                  sx={actionButtonSx}
                  onClick={() =>
                    onAddGame(
                      topParty.id,
                      bottomParty.id,
                      match.name,
                      bracketId,
                      match.id,
                      {
                        venue: match.venue || null,
                        venueName: match.venueName || null,
                        green: match.green || null,
                        rink: match.rink ?? null,
                        gameFormat: match.gameFormat || null
                      }
                    )
                  }>
                  Add Game
                </Button>
              )
            )}
          </>
        ) : null}
        {gameDone && !canBeGame && (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            sx={actionButtonSx}
            onClick={() => onViewGame(game)}>
            View Game
          </Button>
        )}
      </Box>
    </Paper>
  );
});

export default MatchCard;
